import React from 'react';
import { Modal } from '../basic';

interface UploadResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  success: boolean;
  title: string;
  message: string;
  errors?: string[];
  data?: any;
  onViewDetails?: () => void;
}

const UploadResultModal: React.FC<UploadResultModalProps> = ({
  isOpen,
  onClose,
  success,
  title,
  message,
  errors = [],
  data,
  onViewDetails,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            {success ? (
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            ) : (
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900">{title}</h2>
              <p className={`text-sm ${success ? 'text-green-600' : 'text-red-600'}`}>
                {success ? 'Upload completed successfully' : 'Upload failed'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Main Message */}
        <div className="mb-6">
          <div
            className={`p-4 rounded-lg ${
              success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}
          >
            <p className={`text-sm font-medium ${success ? 'text-green-800' : 'text-red-800'}`}>{message}</p>
          </div>
        </div>

        {/* Success Details */}
        {success && data && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Upload Summary</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {data.playersAdded !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">Players Added:</span>
                    <span className="ml-2 text-green-600 font-semibold">{data.playersAdded}</span>
                  </div>
                )}
                {data.playersSkipped !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">Players Skipped:</span>
                    <span className="ml-2 text-blue-600 font-semibold">{data.playersSkipped}</span>
                  </div>
                )}
                {data.gamesAdded !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">Games Added:</span>
                    <span className="ml-2 text-green-600 font-semibold">{data.gamesAdded}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          {onViewDetails && (
            <button
              onClick={onViewDetails}
              className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 transition-colors"
            >
              View Details
            </button>
          )}
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              success ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {success ? 'Continue' : 'Close'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadResultModal;
